import { v4 as uuidv4 } from 'uuid';
import {
  type CreatePricePartParams,
  type CreatePriceOptionParams,
  type ExtendedPriceOption,
} from '../types/PriceFormTypes';

/**
 * Creates a price option with improved parameter structure
 * Replaces the original createPriceOption function with object parameters
 */
export const createPriceOption = ({
  price = null,
  cost = null,
}: CreatePriceOptionParams = {}): ExtendedPriceOption => {
  return {
    name: null,
    uuid: uuidv4(),
    price,
    cost,
    use_custom_quantity: false,
    custom_quantity_unit: null,
    use_minimum_price: false,
    minimum_price: null,
    minimum_cost: null,
    use_tiered_price: false,
    tiered_price_method: null,
    tiered_prices: null,
  };
};

/**
 * Creates a price part with improved parameter structure (alternative implementation)
 * Note: The main createPricePart is exported from PricePartsForm component
 */
export const createPricePartAdvanced = ({
  name = null,
  price = null,
  cost = null,
  order = 0,
}: CreatePricePartParams = {}) => {
  // Handle string to number conversion properly
  const normalizedPrice =
    typeof price === 'string'
      ? price === ''
        ? null
        : parseFloat(price)
      : price;

  return {
    name,
    uuid: uuidv4(),
    optional: false,
    mandatory: false,
    price_options: [createPriceOption({ price: normalizedPrice, cost })],
    order,
  };
};

/**
 * Type-safe helper for setting tiered price method
 * Eliminates need for 'as any' assertions
 */
export const setTieredPriceMethod = (
  option: ExtendedPriceOption,
  method: 'MULTIPLY' | 'ADD_UP' | null
): void => {
  option.tiered_price_method = method;
};

/**
 * Type-safe helper for getting tiered price method
 */
export const getTieredPriceMethod = (
  option: ExtendedPriceOption
): 'MULTIPLY' | 'ADD_UP' | null => {
  return option.tiered_price_method || null;
};
